import type { ComponentPropsOptions } from "vue";
import type { Size } from "@/components/helpers/common-props";
import type { MaskName } from "@/composables/mask";
import type { FlexComponent } from "@/utils/render-component";
import { useVModel } from "@vueuse/core";
import { computed, defineComponent, ref } from "vue";
import { radiusProp, sizeProp, variantProp } from "@/components/helpers/common-props";
import { useMask } from "@/composables/mask";
import { $t } from "@/services/i18n";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { renderComponent } from "@/utils/render-component";
import { fieldCtx } from "../field/Field";
import { formCtx } from "../form/Form";
import IconButton from "../icon-button/IconButton";
import Icon from "../icon/Icon";
import { inputVariants } from "./variants.input";

export const PasswordTypeSwitcher = defineComponent({
  name: "PasswordTypeSwitcher",
  props: {
    modelValue: p.string<"text" | "password">("text"),
  },
  setup(props) {
    const vModel = useVModel(props, "modelValue");

    function toggle() {
      if (vModel.value === "text") {
        vModel.value = "password";
      } else {
        vModel.value = "text";
      }
    }

    const icon = computed<string>(() => {
      if (vModel.value === "text") return "lucide:eye";
      return "lucide:eye-off";
    });

    return () => (
      <IconButton
        size="xs"
        variant="link"
        label={$t("switch_password")}
        class="border-none text-muted-foreground opacity-50 transition-all hover:opacity-100 focus-visible:ring-muted-foreground/50"
        onClick={toggle}
        icon={icon.value}
      />
    );
  },
});

export const inputProps = {
  size: sizeProp,
  radius: radiusProp,
  mask: p.custom<MaskName | (string & {}) | RegExp>([Object, String]),
  type: p.string<"text" | "password" | "email" | "number" | "search">("text"),
  placeholder: p.string(),
  disabled: p.boolean(),
  readonly: p.boolean(),
  required: p.boolean(),
  autofocus: p.boolean(),
  autocomplete: p.string(),
  error: p.boolean(),
  id: p.string(),
  modelValue: p.string(),
  variant: variantProp,
  prefix: p.custom<FlexComponent>([String, Object]),
  suffix: p.custom<FlexComponent>([String, Object]),
  leftIcon: p.string(),
  rightIcon: p.string(),
  maxlength: p.number(),
} satisfies ComponentPropsOptions;

export default defineComponent({
  name: "Input",
  props: inputProps,
  emits: {
    input: (_event: InputEvent) => true,
    blur: (_event: FocusEvent) => true,
    // Declared so callers can guard keys on the interactive element itself
    // instead of a wrapper div (a11y: handlers belong on the input).
    keydown: (_event: KeyboardEvent) => true,
    "update:modelValue": (_value: string) => true,
  },
  setup(props, { attrs, emit }) {
    const form = formCtx.inject(props);
    const field = fieldCtx.inject(props);
    const vModel = useVModel(props, "modelValue");
    const inputRef = useMask(props);

    const internalType = ref("password");

    const type = computed(() => {
      if (props.type === "password") return internalType.value;
      return props.type;
    });

    const prefix = computed(() => {
      if (props.leftIcon) {
        return (
          <Icon icon={props.leftIcon} class="ml-0.5 h-4 w-4 text-muted-foreground opacity-70" />
        );
      }
      return props.prefix;
    });

    const suffix = computed(() => {
      if (props.type === "password") {
        return <PasswordTypeSwitcher v-model={internalType.value} />;
      }

      return props.suffix;
    });

    const size = computed<Size | undefined>(() => {
      return form.size ?? field.size ?? props.size;
    });

    const id = computed(() => {
      return field.id ?? props.id;
    });

    return () => (
      <div class="relative w-full">
        <input
          ref={inputRef}
          v-model={vModel.value}
          id={id.value}
          name={field.name}
          disabled={props.disabled}
          readonly={props.readonly}
          autofocus={props.autofocus}
          autocomplete={props.autocomplete}
          type={type.value}
          data-error={!!field.errorMessage || props.error}
          data-invalid={!!field.errorMessage || props.error}
          maxlength={props.maxlength}
          placeholder={props.placeholder}
          onInput={(e) => emit("input", e as InputEvent)}
          onBlur={(e) => {
            emit("blur", e as FocusEvent);
          }}
          onKeydown={(e) => {
            emit("keydown", e as KeyboardEvent);
          }}
          class={cn(
            inputVariants({
              size: size.value,
              variant: props.variant,
              radius: props.radius,
              suffix: !!suffix.value,
              prefix: !!prefix.value,
            }),
            attrs.class ?? ""
          )}
        ></input>
        {prefix.value && (
          <div class="center absolute inset-y-0 left-0 ml-1 h-full w-8 text-sm">
            {renderComponent(prefix.value)}
          </div>
        )}
        {suffix.value && (
          <div class="center absolute inset-y-0 right-0 mr-1 h-full w-8 text-sm">
            {renderComponent(suffix.value)}
          </div>
        )}
      </div>
    );
  },
});
